import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { describeError } from "@/api/client";
import type { LabelCalibration, Workspace } from "@/api/session";
import { useUpdateWorkspace } from "@/api/workspace";
import {
  Button,
  EmptyState,
  Form,
  FormActions,
  FormSection,
  IconButton,
  InlineAlert,
  NumberField,
  Select,
  TextField,
  UnsavedChangesGuard,
  VisuallyHidden,
  useToast,
} from "@/components";
import { Decimal, type DecimalString } from "@/lib/decimal";
import { labelTemplatesQuery } from "../api";
import styles from "../settings.module.css";

interface CalibrationRow {
  templateId: string;
  dx_mm: DecimalString | null;
  dy_mm: DecimalString | null;
  sx: DecimalString | null;
  sy: DecimalString | null;
}

type CalibrationField = keyof Omit<CalibrationRow, "templateId">;

const NEW_ROW: Omit<CalibrationRow, "templateId"> = { dx_mm: "0", dy_mm: "0", sx: "1", sy: "1" };

const FIELDS: ReadonlyArray<{ field: CalibrationField; label: string; step: number }> = [
  { field: "dx_mm", label: "Offset right (mm)", step: 0.1 },
  { field: "dy_mm", label: "Offset down (mm)", step: 0.1 },
  { field: "sx", label: "Scale across", step: 0.001 },
  { field: "sy", label: "Scale down", step: 0.001 },
];

function rowsFrom(workspace: Workspace): CalibrationRow[] {
  return Object.entries(workspace.settings.label_calibration ?? {})
    .map(([templateId, calibration]) => ({
      templateId,
      dx_mm: String(calibration.dx_mm ?? "0"),
      dy_mm: String(calibration.dy_mm ?? "0"),
      sx: String(calibration.sx ?? "1"),
      sy: String(calibration.sy ?? "1"),
    }))
    .sort((a, b) => a.templateId.localeCompare(b.templateId));
}

function rowError(row: CalibrationRow): string | null {
  for (const key of ["dx_mm", "dy_mm"] as const) {
    const value = Decimal.tryFrom(row[key]);
    if (!value || value.abs().compare("20") > 0) return "Offsets must be between −20 mm and 20 mm.";
  }
  for (const key of ["sx", "sy"] as const) {
    const value = Decimal.tryFrom(row[key]);
    if (!value || value.compare("0.8") < 0 || value.compare("1.2") > 0)
      return "Scale factors must be between 0.8 and 1.2.";
  }
  return null;
}

/**
 * Printer calibration per label template, stored in settings.label_calibration and applied by
 * the labels slice when rendering PDFs: offsets in mm (dx right, dy down) and scale factors.
 */
export function LabelsSection({ workspace, canEdit }: { workspace: Workspace; canEdit: boolean }) {
  const initial = useMemo(() => rowsFrom(workspace), [workspace]);
  const [rows, setRows] = useState(initial);
  const [newTemplate, setNewTemplate] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);
  const templates = useQuery(labelTemplatesQuery);
  const update = useUpdateWorkspace();
  const toast = useToast();

  const dirty = JSON.stringify(rows) !== JSON.stringify(initial);
  const usedIds = new Set(rows.map((row) => row.templateId));
  const templateChoices = (templates.data ?? [])
    .filter((template) => !usedIds.has(template.id))
    .map((template) => ({ value: template.id, label: template.label }));
  const errors = rows.map(rowError);

  function updateRow(templateId: string, field: CalibrationField, value: DecimalString | null) {
    setRows((current) => current.map((row) => (row.templateId === templateId ? { ...row, [field]: value } : row)));
  }

  function addRow() {
    const templateId = newTemplate.trim();
    if (!templateId || usedIds.has(templateId)) return;
    setRows((current) =>
      [...current, { templateId, ...NEW_ROW }].sort((a, b) => a.templateId.localeCompare(b.templateId)),
    );
    setNewTemplate("");
  }

  function handleSubmit() {
    setSubmitted(true);
    if (errors.some(Boolean)) return;
    const calibration: Record<string, LabelCalibration | null> = {};
    for (const row of initial) {
      if (!usedIds.has(row.templateId)) calibration[row.templateId] = null;
    }
    for (const row of rows) {
      calibration[row.templateId] = {
        dx_mm: Decimal.from(row.dx_mm ?? "0")
          .normalize()
          .toString(),
        dy_mm: Decimal.from(row.dy_mm ?? "0")
          .normalize()
          .toString(),
        sx: Decimal.from(row.sx ?? "1")
          .normalize()
          .toString(),
        sy: Decimal.from(row.sy ?? "1")
          .normalize()
          .toString(),
      };
    }
    update.mutate(
      { settings: { label_calibration: calibration } },
      {
        onSuccess: (saved) => {
          setRows(rowsFrom(saved));
          setSubmitted(false);
          toast.success("Label calibration saved");
        },
      },
    );
  }

  return (
    <Form onSubmit={handleSubmit} aria-label="Label calibration">
      <UnsavedChangesGuard when={dirty && canEdit} subject="your calibration changes" />
      {update.isError ? (
        <InlineAlert tone="danger" title="Couldn’t save the calibration">
          {describeError(update.error)}
        </InlineAlert>
      ) : null}
      <FormSection
        title="Label calibration"
        description="Print a calibration sheet from Labels, measure how far the print sits from the label edges, and enter the correction here. Offsets move the print right and down; scale stretches it."
      />

      {rows.length ? (
        <table className={styles.calibrationTable}>
          <caption>
            <VisuallyHidden>Calibration per label template</VisuallyHidden>
          </caption>
          <thead>
            <tr>
              <th scope="col">Template</th>
              {FIELDS.map(({ field, label }) => (
                <th key={field} scope="col" data-numeric="">
                  {label}
                </th>
              ))}
              <th scope="col">
                <VisuallyHidden>Remove</VisuallyHidden>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.templateId}>
                <td>
                  <span className={styles.templateId}>{row.templateId}</span>
                  {submitted && errors[index] ? <p className={styles.hint}>{errors[index]}</p> : null}
                </td>
                {FIELDS.map(({ field, label, step }) => (
                  <td key={field} className={styles.numberCell}>
                    <NumberField
                      label={`${label} for ${row.templateId}`}
                      hideLabel
                      size="sm"
                      step={step}
                      value={row[field]}
                      onValueChange={(value) => updateRow(row.templateId, field, value)}
                      disabled={!canEdit}
                    />
                  </td>
                ))}
                <td>
                  <IconButton
                    icon="trash"
                    label={`Remove calibration for ${row.templateId}`}
                    size="sm"
                    disabled={!canEdit}
                    onClick={() =>
                      setRows((current) => current.filter((candidate) => candidate.templateId !== row.templateId))
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          variant="inline"
          headingLevel={3}
          icon="tag"
          title="No calibration yet"
          description="Labels print with no correction until you add a template here."
        />
      )}

      {canEdit ? (
        <div className={styles.addRow}>
          {templateChoices.length ? (
            <Select
              label="Add a template"
              options={templateChoices}
              value={newTemplate || null}
              onValueChange={(value) => setNewTemplate(value ?? "")}
              placeholder="Choose a template…"
            />
          ) : (
            <TextField
              label="Add a template"
              description="Template id as listed in Labels, e.g. avery-5195."
              value={newTemplate}
              onValueChange={setNewTemplate}
              mono
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addRow();
                }
              }}
            />
          )}
          <Button icon="plus" onClick={addRow} disabled={!newTemplate.trim() || usedIds.has(newTemplate.trim())}>
            Add calibration
          </Button>
        </div>
      ) : null}

      <FormActions>
        <Button
          type="submit"
          variant="primary"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={!canEdit || !dirty}
        >
          Save calibration
        </Button>
        {dirty ? (
          <Button variant="ghost" onClick={() => setRows(initial)}>
            Discard changes
          </Button>
        ) : null}
      </FormActions>
    </Form>
  );
}
